import { useState } from "react";

import { Optional } from "common/utils/types";
import { useFormState } from "common/hooks";
import { CORE_PHONE_LENGTH } from "common/constants";
import { parseNumber } from "common/utils/numbers";
import { toPhone } from "common/utils/formatting";
import { useNavigate } from "common/router/hooks";
import { UseQueryResultV5 } from "common/queries/types";

import PhoneInput from "client/common/components/inputs/PhoneInput";
import Button from "client/common/components/buttons/Button";
import Form from "client/common/components/forms/Form";
import TextInput from "client/common/components/inputs/TextInput";
import TextButton from "client/common/components/buttons/TextButton";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import PasswordInput from "client/common/components/inputs/PasswordInput";
import Tooltip from "client/common/components/content/Tooltip";
import { urls } from "client/routes";

import { usernameLockTooltip, passwordConditions } from "../../utils";
import {
  useLoginPhoneNumberValidateQuery,
  useProfileLookupMutation,
  useSendVerificationCodeMutation,
} from "../../hooks";
import VerifySmsSignupModal from "./VerifySmsSignupModal";
import {
  AuthenticationRedirectAfterLookup,
  ILoginPhoneValidationResults,
  SignupFormValues,
} from "../../types";
import AccountCreationLegalDisclaimer from "./AccountCreationDisclaimer";
import SmsLegalDisclaimer from "./SmsLegalDisclaimer";

import styles from "../../styles/CreateAccountSmsAuthForm.module.less";

const genPhoneInputStatus = (phoneQuery: UseQueryResultV5<ILoginPhoneValidationResults>) => {
  if (phoneQuery.isLoading) {
    return "loading";
  }
  if (phoneQuery.isSuccess) {
    return phoneQuery.data?.is_valid ? "success" : "error";
  }
  return "idle";
};

type CreateAccountSmsAuthFormProps = {
  initialPhone: Optional<string | number>;
  initialEmail: Optional<string>;
  initialFirstName: Optional<string>;
  initialLastName: Optional<string>;
  next?: string;
  /** Lock the phone (username) field to the on-file contact when it's on file. */
  lockUsername: boolean;
  /** Whether the user may switch to the email signup form (false when pinned to SMS). */
  canSwitchChannel: boolean;
};

const CreateAccountSmsAuthForm = (props: CreateAccountSmsAuthFormProps) => {
  const {
    initialPhone = "",
    initialEmail = "",
    initialFirstName = "",
    initialLastName = "",
    next,
    lockUsername,
    canSwitchChannel,
  } = props;

  const navigate = useNavigate();

  const [isConfirming, setIsConfirming] = useState(false);

  const [values, updateField] = useFormState<SignupFormValues>({
    first_name: initialFirstName ?? "",
    last_name: initialLastName ?? "",
    phone: initialPhone ? initialPhone.toString() : "",
    email: initialEmail ?? "",
    password: "",
  });

  const isPhoneComplete = values.phone.length >= CORE_PHONE_LENGTH;

  const validateQuery = useLoginPhoneNumberValidateQuery(
    { number: values.phone },
    { enabled: isPhoneComplete },
  );
  const {
    data: { is_valid: isValid = true, detail: validationDetail } = {},
    error: validationError,
  } = validateQuery;

  const {
    mutate: sendCode,
    isLoading: isSendingCode,
    error: sendingCodeError,
  } = useSendVerificationCodeMutation({
    // Show the confirmation modal once the code has been sent
    onSuccess: () => setIsConfirming(true),
    signupTracking: { usernameType: "phone", isResend: false },
  });

  const exhaustiveCheck = (redirect: never) => {
    console.warn(`Attempted to get authentication redirect for invalid value: ${redirect}`);
  };

  const {
    mutate: lookupProfile,
    isLoading: isLookingUp,
    error: lookupError,
  } = useProfileLookupMutation({
    onSuccess: (result) => {
      switch (result.authentication_redirect) {
        case AuthenticationRedirectAfterLookup.SigninPhone:
          return navigate(
            urls.accounts.login({
              search: {
                flash: `An account already exists for ${toPhone(result.number)}. Please log in, or contact support if you need help signing up.`,
                phone: `${result.number}`,
              },
            }),
          );
        case AuthenticationRedirectAfterLookup.SigninEmail:
          return navigate(
            urls.accounts.login({
              search: {
                flash: `An account already exists for ${result.email}. Please log in, or contact support if you need help signing up.`,
                email: `${result.email}`,
              },
            }),
          );
        case AuthenticationRedirectAfterLookup.Verification:
          return sendCode({
            number: result.number,
            profile: {
              first_name: values.first_name,
              last_name: values.last_name,
              new_password: values.password,
            },
          });
        default:
          return exhaustiveCheck(result.authentication_redirect);
      }
    },
  });

  const error = lookupError || validationError || sendingCodeError;
  const validationErrorDetails = validationDetail ? [validationDetail] : undefined;
  const isFormComplete = !!(
    values.first_name &&
    values.last_name &&
    values.email &&
    isPhoneComplete &&
    passwordConditions.every(({ check }) => check(values.password))
  );

  const canSubmit = isFormComplete && !isLookingUp && !isSendingCode && isValid;
  const isSubmitting = isLookingUp || isSendingCode;
  const onSubmit = () => {
    lookupProfile({
      email: values.email,
      number: parseNumber(values.phone, 0),
      check_sms: true,
    });
  };

  const phoneErrorVal =
    lookupError?.errors.number || validationErrorDetails || sendingCodeError?.errors.number;
  const phoneError = phoneErrorVal
    ? [
        {
          key: "phoneError",
          content: (
            <div className={styles.form}>
              <div>{phoneErrorVal}</div>
              {canSwitchChannel && (
                <div>
                  Can&apos;t receive text messages?{" "}
                  <TextButton
                    hasUnderline={true}
                    onClick={() => {
                      navigate(
                        urls.accounts.emailSignup({
                          search: {
                            phone: values.phone,
                            email: values.email,
                            first_name: values.first_name,
                            last_name: values.last_name,
                            next,
                          },
                        }),
                      );
                    }}
                  >
                    Sign up with email
                  </TextButton>
                  .
                </div>
              )}
            </div>
          ),
        },
      ]
    : undefined;

  return (
    <>
      <Form className={styles.form} canSubmit={canSubmit} onSubmit={onSubmit}>
        {!!error && <FetchErrorAlert error={error} />}
        <div className={styles.names}>
          <TextInput
            label="First Name"
            value={values.first_name}
            onChange={updateField("first_name")}
            errors={sendingCodeError?.errors.profile?.first_name}
          />
          <TextInput
            label="Last Name"
            value={values.last_name}
            onChange={updateField("last_name")}
            errors={sendingCodeError?.errors.profile?.last_name}
          />
        </div>
        <Tooltip isEnabled={lockUsername} content={usernameLockTooltip}>
          <PhoneInput
            label="Mobile Number"
            value={values.phone}
            onChange={updateField("phone")}
            status={genPhoneInputStatus(validateQuery)}
            errors={phoneError}
            disabled={lockUsername}
          />
        </Tooltip>
        <TextInput
          label="Email"
          value={values.email}
          onChange={updateField("email")}
          errors={lookupError?.errors.email}
        />
        <PasswordInput
          value={values.password}
          onChange={updateField("password")}
          passwordConditions={passwordConditions}
          errors={sendingCodeError?.errors.profile?.new_password}
        />
        <Button type="submit" disabled={!canSubmit} isLoading={isSubmitting}>
          Sign Up
        </Button>
        {canSwitchChannel && (
          <div className={styles.help}>
            Can&apos;t receive text messages? Sign up with{" "}
            <TextButton
              hasUnderline={true}
              onClick={() => {
                navigate(
                  urls.accounts.emailSignup({
                    search: {
                      phone: values.phone,
                      email: values.email,
                      first_name: values.first_name,
                      last_name: values.last_name,
                      next,
                    },
                  }),
                );
              }}
            >
              email
            </TextButton>
            .
          </div>
        )}
        <AccountCreationLegalDisclaimer textAlign="center" />
        <SmsLegalDisclaimer textAlign="center" />
      </Form>
      <VerifySmsSignupModal
        visible={isConfirming}
        onClose={() => setIsConfirming(false)}
        values={values}
        next={next}
        lockUsername={lockUsername}
        canSwitchChannel={canSwitchChannel}
        onTryDifferentNumber={() => {
          updateField("phone")("");
        }}
        onSendToEmail={() => {
          navigate(
            urls.accounts.emailSignup({
              search: {
                phone: values.phone,
                email: values.email,
                first_name: values.first_name,
                last_name: values.last_name,
                next,
              },
            }),
          );
        }}
      />
    </>
  );
};

export default CreateAccountSmsAuthForm;
